/*
 * Copyright (c) 2019. World Wide Generation Ltd
 */

import React, { useEffect, useMemo, useState } from 'react';
import { Button } from 'reactstrap';
import { loadSurvey, reloadSurveyListSummary } from '../../../actions/survey';
import { saveUniversalTrackerValue, UpdateActions } from '../../../actions/universalTrackerValue';
import { TOOLTIP } from '../../../constants/labels';
import { NotApplicableTypes } from '../../../constants/status';
import { SurveyActionData } from '../../../model/surveyData';
import { useAppDispatch, useAppSelector } from '../../../reducers';
import { getCurrentUser } from '../../../selectors/user';
import { AnalyticsEvents } from '../../../services/analytics/AnalyticsEvents';
import { getAnalytics } from '../../../services/analytics/AnalyticsService';
import G17Client, { FlagProperties } from '../../../services/G17Client';
import { SurveyPermissions } from '../../../services/permissions/SurveyPermissions';
import { addSiteAlert, SiteAlertColors } from '../../../slice/siteAlertsSlice';
import { UniversalTrackerValuePlain } from '../../../types/surveyScope';
import { SubmitQuestionData } from '../../../utils/files';
import DelegationModal from '../../delegation-modal';
import { BlockingLoader } from '../../loader/BlockingLoader';
import { useToggle } from '../../../hooks/useToggle';
import { BulkButton, ToggledButton, countQuestions } from './ToggledButton';
import { ActionsMenu } from './ActionsMenu';
import './BulkActionToolbar.scss';
import { FeaturePermissions } from '../../../services/permissions/FeaturePermissions';
import { DecimalButton } from '../../utr-decimal/DecimalButton';
import { UniversalTrackerBlueprintMin, ValueValidation } from '../../../types/universalTracker';
import { getNumericSelectedQuestions } from '../utils';
import { CongratulationsModal } from '../../complete-survey-modal/CongratulationsModal';
import { useToggleCompleteSurveys } from '../../../hooks/useToggleCompleteSurveys';
import { useToggleCongratulationsModal } from '../../complete-survey-modal/useToggleCongratulationsModal';
import { TagButton } from '../../tags/TagButton';
import { QUESTION } from '../../../constants/terminology';

export type BulkActionToolbarSelectedUtrv = Pick<
  UniversalTrackerValuePlain,
  | '_id'
  | 'evidenceRequired'
  | 'verificationRequired'
  | 'noteRequired'
  | 'isPrivate'
  | 'status'
  | 'assuranceStatus'
  | 'stakeholders'
  | 'initiativeId'
  | 'universalTrackerId'
  | 'valueType'
> & { valueValidation?: ValueValidation };

export type BulkActionUtr = Pick<UniversalTrackerBlueprintMin, '_id' | 'valueValidation'> & { valueType?: string };

export interface BulkActionToolbarProps {
  surveyId: string;
  initiativeId: string;
  selectedQuestions: BulkActionToolbarSelectedUtrv[];
  triggerBulkAction: (properties: FlagProperties) => void;
  toggleSelectAll: () => void;
  handleClose: () => void;
  questionCount: number;
  handleReload?: () => void;
  hiddenOptions?: ('decimal' | 'tag')[];
  surveyData: Pick<
    SurveyActionData,
    '_id' | 'roles' | 'stakeholders' | 'initiativeId' | 'fragmentUniversalTrackerValues'
  >;
}

export const BulkActionToolbar = (props: BulkActionToolbarProps) => {
  const {
    selectedQuestions,
    toggleSelectAll,
    handleClose,
    surveyId,
    surveyData,
    initiativeId,
    questionCount,
    hiddenOptions = [],
  } = props;
  const dispatch = useAppDispatch();
  const canAccessVerification = useAppSelector(FeaturePermissions.canAccessVerification);

  const [delegationModal, setDelegationModal] = useState<BulkActionToolbarSelectedUtrv[]>([]);
  const [tagModal, setOpenTagModal] = useState<BulkActionUtr[]>([]);
  const [selectedQuestionsDecimal, setSelectedQuestionsDecimal] = useState<BulkActionUtr[]>([]);
  const [openActionsMenu, toggleActionsMenu, setOpenActionsMenu] = useToggle(false);
  const [isLoading, setLoading] = useState(false);
  const currentUser = useAppSelector(getCurrentUser);

  const canContribute = currentUser && SurveyPermissions.canContribute(surveyData, currentUser);

  const selectedCount = useMemo(() => countQuestions(selectedQuestions), [selectedQuestions]);
  const className = selectedCount > 0 ? 'toolbarShow' : 'toolbarHide';

  const handleToggleTagModal = () => setOpenTagModal([]);

  useEffect(() => {
    const zE = window.zE;
    if (zE) {
      // Hide when selected count is more than 0
      zE('messenger:set', 'zIndex', selectedCount > 0 ? -1 : 1000);
      // Still need to restore the widget at the end
      return () => zE('messenger:set', 'zIndex', 1000);
    }
  }, [selectedCount])

  const getSelectedUtrs = () =>
    selectedQuestions.map((utrv) => ({
      _id: utrv.universalTrackerId,
      valueType: utrv.valueType,
      valueValidation: utrv.valueValidation,
    }));

  const handleReload = async () => {
    return Promise.all([
      dispatch(loadSurvey(surveyId, false, true)),
      dispatch(reloadSurveyListSummary()),
    ])
      .then(() => props.handleReload?.());
  }

  const { toggleCompleteSurveys } = useToggleCompleteSurveys({
    handleReload,
    surveys: [surveyData],
    isCompleting: true,
  });

  const { isCongratulationsModalOpen, openCongratulationsModal, toggleCongratulationsModal } =
    useToggleCongratulationsModal({ survey: surveyData, currentUser, toggleCompleteSurveys });

  if (!currentUser) {
    return null;
  }

  const handleCatch = (e: string) => {
    setLoading(false);
    dispatch(addSiteAlert({
      content: `Could not update ${QUESTION.PLURAL}`,
      color: SiteAlertColors.Danger
    }));
  }

  const handleAddTagReload = async ({ reloadSurvey = true }: { reloadSurvey?: boolean } = {}) => {
    handleToggleTagModal();
    if (!reloadSurvey) {
      return;
    }
    setLoading(true);
    await handleReload();
    setLoading(false);
  };

  const handleCloseSettingDecimal = async ({ reloadSurvey = false }: { reloadSurvey?: boolean } = {}) => {
    setSelectedQuestionsDecimal([]);
    if (!reloadSurvey) {
      return;
    }
    setLoading(true);
    await handleReload();
    setLoading(false);
  };

  function handleBulkFlagChange(properties: FlagProperties, utrvIds: string[]) {
    if (!canContribute || !surveyId) {
      return;
    }
    setLoading(true);
    return G17Client.updateSurveyQuestionFlags({ surveyId, utrvIds, properties })
      .then(() => {
        getAnalytics().track(AnalyticsEvents.SurveyBulkAction, {
          utrvIds,
          properties,
          surveyId,
          initiativeId
        });
        props.triggerBulkAction(properties);
        return handleReload();
      })
      .then(() => {
        setLoading(false);
        setOpenActionsMenu(false);
      })
      .catch(handleCatch)
  }

  const handleBulkNAByType = (naType: string) => {
    if (!canContribute) {
      return;
    }
    const handleSubmitQuestion = (utrvId: string, notApplicableType: string) => {
      const data: SubmitQuestionData = {
        autoVerify: 'true',
        valueData: { notApplicableType, input: {} },
      };
      return dispatch(saveUniversalTrackerValue(UpdateActions.STATUS.UPDATE, utrvId, data)) as unknown as Promise<unknown>;
    }

    setLoading(true);
    Promise.all(selectedQuestions.map(selectedQuestion => handleSubmitQuestion(selectedQuestion._id, naType)))
      .then(() => handleReload())
      .then(() => {
        openCongratulationsModal({ editingUtrvIds: selectedQuestions.map(({_id}) => _id), verified: true })
      })
      .then(() => setLoading(false))
      .catch(handleCatch)
  }

  function downloadCurrentQuestionCsv() {
    if (surveyId) {
      const ids = selectedQuestions.map(u => u._id)
      return G17Client.downloadSurveyQuestions(surveyId, ids);
    }
  }

  const canManage = surveyData && SurveyPermissions.canManage(surveyData, currentUser);

  const toggledButtonCommon = {
    selectedQuestions,
    handleBulkFlagChange
  }

  const PrivacyButton = () => canManage ? ToggledButton({ ...toggledButtonCommon, field: 'isPrivate', action: 'Make public', reverseAction: 'Make private' }) : null;

  const areAllSelected = selectedCount > 0 && questionCount === selectedCount;
  return (
    <>
      {isLoading ? <BlockingLoader /> : null}
      <div className={`bulk-action-toolbar ${className}`}>
        <div className='bulk-action-contents d-flex justify-content-center '>
          <div className='mr-auto'></div>
          <div>
            <PrivacyButton />
            {canContribute ? (
              <BulkButton tooltip={TOOLTIP.notReporting} onClick={() => handleBulkNAByType(NotApplicableTypes.nr)}>
                Mark N/R
              </BulkButton>
            ) : null}
            {canContribute ? (
              <BulkButton tooltip={TOOLTIP.notApplicable} onClick={() => handleBulkNAByType(NotApplicableTypes.na)}>
                Mark N/A
              </BulkButton>
            ) : null}
            <BulkButton
              tooltip='Download'
              className={'download-button-questions ml-2'}
              onClick={() => downloadCurrentQuestionCsv()}
            >
              Download
            </BulkButton>
            <DecimalButton
              isHidden={hiddenOptions.includes('decimal')}
              selectedQuestions={selectedQuestionsDecimal}
              handleClick={() => setSelectedQuestionsDecimal(getSelectedUtrs())}
              handleClose={handleCloseSettingDecimal}
              numericSelectedQuestions={getNumericSelectedQuestions(
                selectedQuestions.length > 0 ? getSelectedUtrs() : selectedQuestionsDecimal
              )}
              initiativeId={initiativeId}
            />
            {!hiddenOptions.includes('tag') ? (
              <TagButton
                onClick={() =>setOpenTagModal(getSelectedUtrs())}
                selectedQuestions={tagModal}
                handleReload={handleAddTagReload}
                onToggle={handleToggleTagModal}
              />
            ) : null}
            {canManage ? (
              <BulkButton tooltip='Delegate' onClick={() => setDelegationModal(selectedQuestions)}>
                Delegate
              </BulkButton>
            ) : null}
            {canManage ? (
              <ActionsMenu
                {...toggledButtonCommon}
                openMenu={openActionsMenu}
                toggleMenu={toggleActionsMenu}
                canAccessVerification={canAccessVerification}
              />
            ) : null}
          </div>
          <div className='ml-2 d-flex align-items-center flex-column flex-lg-row text-nowrap'>
            <div>
              <span className='p-1 text-ThemeTextWhite d-inline-block' style={{ minWidth: '80px' }}>
                {selectedCount} selected
              </span>
            </div>
            <div>
              <Button
                color='link'
                size='sm'
                onClick={toggleSelectAll}
                className='text-ThemeTextWhite'
                style={{ textDecoration: 'underline' }}
              >
                {areAllSelected ? 'Unselect all' : `Select all (${questionCount})`}
              </Button>
            </div>
          </div>
          <div className='ml-auto d-flex align-items-center'>
            <Button color='link' onClick={handleClose}>
              <i className='fa fa-times text-ColourWhite' />
            </Button>
          </div>
        </div>
      </div>

      {delegationModal.length > 0 ? (
        <DelegationModal
          isOpen={delegationModal.length > 0}
          survey={surveyData}
          handleSubmit={async () => {
            setLoading(true);
            await handleReload();
            setLoading(false);
          }}
          toggle={() => setDelegationModal([])}
          initiativeId={initiativeId}
          utrvs={delegationModal}
        />
      ) : null}
      <CongratulationsModal isOpen={isCongratulationsModalOpen} toggle={toggleCongratulationsModal} />
    </>
  );
};
